<template>
  <div v-if="isOpen" class="fixed inset-0 overflow-hidden z-[70]" @keydown.esc="$emit('close')">
    <div class="absolute inset-0 transition-opacity bg-gray-900/60 backdrop-blur-sm" @click="$emit('close')" style="z-index: 60;"></div>

    <div class="fixed inset-0 flex items-center justify-center p-4" style="z-index: 60;">
      <div class="w-full h-full max-w-7xl max-h-[90vh] mx-auto">
        <div class="h-full flex flex-col bg-white shadow-2xl rounded-xl overflow-hidden">

          <!-- Header -->
          <div class="p-4 bg-white border-b border-gray-100">
            <div class="flex items-start justify-between">
              <div class="flex-1">
                <div class="flex items-center space-x-4">
                  <div class="w-10 h-10 bg-blue-50 rounded-xl flex items-center justify-center flex-shrink-0">
                    <svg class="w-6 h-6 text-blue-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                    </svg>
                  </div>
                  <div>
                    <h2 class="text-xl font-semibold text-gray-900">Registro de actividad</h2>
                    <p class="mt-1 text-sm text-gray-500">Historial completo de acciones realizadas en el sistema</p>
                    <span class="text-sm font-semibold text-blue-600">{{ totalRegistros }}</span>
                    <span class="text-sm text-blue-600 "> registros encontrados</span>
                  </div>
                </div>
              </div>
              <button @click="$emit('close')" class="ml-4 rounded-full text-gray-400 hover:text-gray-500 hover:bg-gray-100 p-1.5 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-300">
                <svg class="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
                </svg>
              </button>
            </div>

            <!-- Filtros y búsqueda -->
            <div class="mt-4 space-y-4">
              <!-- Barra de búsqueda -->
              <div class="relative">
                <div class="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
                  <svg class="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path>
                  </svg>
                </div>
                <input v-model="searchQuery" type="text" placeholder="Buscar por usuario, acción o detalles..." class="block w-full pl-10 pr-3 py-2.5 border border-gray-200 rounded-lg placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 text-sm" @input="handleSearch" />
              </div>

              <!-- Filtros de tipo de actividad -->
              <div>
                <div class="flex flex-wrap gap-2">
                  <button v-for="filtro in filtrosDisponibles" :key="filtro.value" @click="toggleFiltro(filtro.value)" :class="[
                    'px-3 py-1.5 rounded-full text-sm font-medium transition-all duration-200 flex items-center space-x-2 border',
                    filtrosActivos.includes(filtro.value)
                      ? 'bg-blue-600 text-white hover:bg-blue-700 border-transparent'
                      : 'bg-white text-gray-600 hover:bg-gray-50 border-gray-200'
                  ]">
                    <span>{{ filtro.label }}</span>
                    <span v-if="filtro.count > 0" :class="[
                      'px-2 py-0.5 rounded-full text-xs font-semibold',
                      filtrosActivos.includes(filtro.value)
                        ? 'bg-white/20 text-white'
                        : 'bg-gray-100 text-gray-600'
                    ]">
                      {{ filtro.count }}
                    </span>
                  </button>
                </div>
              </div>
            </div>
          </div>

          <!-- Contenido - Registros -->
          <div class="flex-1 overflow-y-auto px-6 pb-6 bg-gray-50">
            <!-- Loading -->
            <div v-if="cargando" class="flex items-center justify-center py-12">
              <div class="flex items-center space-x-3">
                <svg class="animate-spin h-8 w-8 text-blue-600" fill="none" viewBox="0 0 24 24">
                  <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                  <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                <span class="text-gray-600 text-lg">Cargando registros...</span>
              </div>
            </div>

            <!-- Sin registros -->
            <div v-else-if="registrosFiltrados.length === 0" class="text-center py-16">
              <svg class="mx-auto h-16 w-16 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
              </svg>
              <h3 class="mt-4 text-lg font-medium text-gray-900">No hay registros</h3>
              <p class="mt-2 text-sm text-gray-500">
                {{ searchQuery ? 'No se encontraron registros con ese criterio de búsqueda' : 'Aún no hay actividad registrada' }}
              </p>
            </div>

            <!-- Registros agrupados -->
            <div v-else class="space-y-8">
              <div v-for="grupo in registrosAgrupados" :key="grupo.fecha" class="space-y-4">
                <!-- Encabezado de grupo -->
                <div class="sticky top-0 bg-gray-50 py-3 z-50 -mx-6 px-6 backdrop-blur-md border-b border-gray-200">
                  <div class="flex items-center space-x-3">
                    <h3 class="text-base font-semibold text-gray-900">{{ grupo.label }}</h3>
                    <span class="px-3 py-1 bg-blue-100 text-blue-800 text-xs font-medium rounded-full">
                      {{ grupo.registros.length }} registro{{ grupo.registros.length !== 1 ? 's' : '' }}
                    </span>
                  </div>
                </div>

                <!-- Grid de registros -->
                <div class="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  <div v-for="registro in grupo.registros" :key="registro.id" class="bg-white border border-gray-200 rounded-xl p-4 transition-shadow duration-200 hover:shadow-md">
                    <div class="flex items-start space-x-4">
                      <!-- Avatar con iniciales -->
                      <div class="flex-shrink-0">
                        <div :class="[
                          'w-10 h-10 rounded-full flex items-center justify-center text-white font-semibold text-base',
                          getColorForAction(registro.action)
                        ]">
                          {{ getInitials(registro.user) }}
                        </div>
                      </div>

                      <!-- Contenido del registro -->
                      <div class="flex-1 min-w-0">
                        <div class="flex items-start justify-between mb-1.5">
                          <div>
                            <p class="text-sm font-semibold text-gray-900 mb-0.5">
                              {{ registro.user }}
                            </p>
                            <span :class="[
                              'inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium',
                              getBadgeClass(registro.action)
                            ]">
                              {{ getActionLabel(registro.action) }}
                            </span>
                          </div>
                          <span class="text-xs text-gray-500 whitespace-nowrap ml-2 tracking-tight">
                            {{ formatTimeAgo(registro.timestamp) }}
                          </span>
                        </div>

                        <p class="mt-1.5 mb-3 text-sm text-gray-700 line-clamp-2">
                          {{ registro.description }}
                        </p>

                        <div class="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-500">
                          <div class="flex items-center space-x-1">
                            <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
                            </svg>
                            <span>{{ registro.fecha_accion }}</span>
                          </div>
                          <span class="text-gray-400">•</span>
                          <span>{{ registro.location }}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Botón cargar más -->
            <div v-if="hasMore && !cargando" class="mt-8 pb-6 text-center">
              <button @click="cargarMasRegistros" class="inline-flex items-center px-6 py-3 border border-gray-300 shadow-sm text-sm font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 hover:border-blue-500 hover:text-blue-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all">
                <svg class="mr-2 h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"></path>
                </svg>
                Cargar más registros
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
// 🔥 IMPORTAR EL SERVICIO REAL
import HistorialService from '@/services/usuarios/historial.js'

export default {
  name: 'RegistroActividad',
  props: {
    isOpen: {
      type: Boolean,
      default: false
    },
    // Opcional: filtrar por usuario específico
    userId: {
      type: Number,
      default: null
    }
  },
  data() {
    return {
      registros: [],
      cargando: false,
      searchQuery: '',
      filtrosActivos: [],
      totalRegistros: 0,
      hasMore: false,
      page: 1,
      registrosPorPagina: 50,

      filtrosDisponibles: [
        { label: 'Inicios de sesión', value: 'inicio_sesion', count: 0 },
        { label: 'Cierres de sesión', value: 'cierre_sesion', count: 0 },
        { label: 'Usuarios creados', value: 'usuario_creado', count: 0 },
        { label: 'Modificaciones', value: 'modificacion', count: 0 },
        { label: 'Permisos', value: 'permiso', count: 0 },
        { label: 'Eliminaciones', value: 'eliminacion', count: 0 },
        { label: 'Configuración', value: 'configuracion', count: 0 }
      ]
    }
  },
  computed: {
    registrosFiltrados() {
      let registros = [...this.registros]

      // Filtrar por tipo de acción
      if (this.filtrosActivos.length > 0) {
        registros = registros.filter(r => this.filtrosActivos.includes(r.action))
      }

      // Filtrar por búsqueda
      if (this.searchQuery) {
        const query = this.searchQuery.toLowerCase()
        registros = registros.filter(r =>
          r.user.toLowerCase().includes(query) ||
          r.description.toLowerCase().includes(query) ||
          r.ip.includes(query) ||
          r.location.toLowerCase().includes(query)
        )
      }

      return registros
    },

    registrosAgrupados() {
      return HistorialService.agruparPorFecha(this.registrosFiltrados)
    }
  },
  watch: {
    isOpen(newVal) {
      if (newVal) {
        this.cargarRegistros()
        document.body.style.overflow = 'hidden'
      } else {
        document.body.style.overflow = ''
      }
    }
  },
  methods: {
    // 🔥 CARGAR REGISTROS DESDE EL SERVICIO REAL
    async cargarRegistros() {
      this.cargando = true
      try {

        let registros = []

        // Si hay userId específico, cargar solo ese usuario
        if (this.userId) {
          registros = await HistorialService.obtenerHistorialUsuario(this.userId)
        } else {
          // Cargar todo el historial
          registros = await HistorialService.obtenerHistorialCompleto()
        }

        // Asignar registros
        this.registros = registros
        this.totalRegistros = registros.length

        // Actualizar conteos de filtros
        this.actualizarConteosFiltros()

        // Determinar si hay más registros (paginación futura)
        this.hasMore = false // TODO: Implementar paginación si tu backend lo soporta

      } catch (error) {
        console.error('❌ Error cargando registros:', error)
        this.$emit('error', error.message)
        // Puedes agregar un toast/notificación aquí
      } finally {
        this.cargando = false
      }
    },

    // Actualizar conteos en los filtros
    actualizarConteosFiltros() {
      const conteos = HistorialService.obtenerConteosPorAccion(this.registros)

      this.filtrosDisponibles.forEach(filtro => {
        filtro.count = conteos[filtro.value] || 0
      })
    },

    async cargarMasRegistros() {
      this.page++
      console.log('📄 Cargando página', this.page)
      // TODO: Implementar paginación si tu backend lo soporta
    },

    toggleFiltro(valor) {
      const index = this.filtrosActivos.indexOf(valor)
      if (index > -1) {
        this.filtrosActivos.splice(index, 1)
      } else {
        this.filtrosActivos.push(valor)
      }
    },

    async handleSearch() {
      // La búsqueda se maneja automáticamente mediante el computed
      console.log('🔍 Buscando:', this.searchQuery)
    },

    // Forzar actualización del historial
    async actualizarHistorial() {
      console.log('🔄 Forzando actualización del historial...')
      await this.cargarRegistros()
    },

    getInitials(email) {
      if (!email) return 'U'
      return email.charAt(0).toUpperCase()
    },

    getColorForAction(action) {
      const colors = {
        'usuario_creado': 'bg-green-500',
        'modificacion': 'bg-blue-500',
        'permiso': 'bg-purple-500',
        'inicio_sesion': 'bg-teal-500',
        'cierre_sesion': 'bg-gray-500',
        'eliminacion': 'bg-red-500',
        'configuracion': 'bg-orange-500'
      }
      return colors[action] || 'bg-gray-500'
    },

    getBadgeClass(action) {
      const classes = {
        'usuario_creado': 'bg-green-100 text-green-800',
        'modificacion': 'bg-blue-100 text-blue-800',
        'permiso': 'bg-purple-100 text-purple-800',
        'inicio_sesion': 'bg-teal-100 text-teal-800',
        'cierre_sesion': 'bg-gray-100 text-gray-800',
        'eliminacion': 'bg-red-100 text-red-800',
        'configuracion': 'bg-orange-100 text-orange-800'
      }
      return classes[action] || 'bg-gray-100 text-gray-800'
    },

    getActionLabel(action) {
      const labels = {
        'usuario_creado': 'Usuario creado',
        'modificacion': 'Modificación',
        'permiso': 'Permiso',
        'inicio_sesion': 'Inicio de sesión',
        'cierre_sesion': 'Cierre de sesión',
        'eliminacion': 'Eliminación',
        'configuracion': 'Configuración'
      }
      return labels[action] || action
    },

    formatTimeAgo(timestamp) {
      const now = new Date()
      const diff = now - new Date(timestamp)
      const minutes = Math.floor(diff / 60000)
      const hours = Math.floor(diff / 3600000)
      const days = Math.floor(diff / 86400000)

      if (minutes < 1) return 'Justo ahora'
      if (minutes < 60) return `Hace ${minutes} minuto${minutes !== 1 ? 's' : ''}`
      if (hours < 24) return `Hace ${hours} hora${hours !== 1 ? 's' : ''}`
      if (days < 7) return `Hace ${days} día${days !== 1 ? 's' : ''}`

      return new Date(timestamp).toLocaleDateString('es-CL', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    }
  },

  beforeUnmount() {
    document.body.style.overflow = ''
  }
}
</script>

<style scoped>
/* Animaciones suaves para fullscreen */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

/* Animación de zoom-in para modal fullscreen */
@keyframes modalZoomIn {
  from {
    opacity: 0;
    transform: scale(0.95);
  }

  to {
    opacity: 1;
    transform: scale(1);
  }
}

@keyframes modalZoomOut {
  from {
    opacity: 1;
    transform: scale(1);
  }

  to {
    opacity: 0;
    transform: scale(0.95);
  }
}

.fixed.inset-0>div>div {
  animation: modalZoomIn 0.3s ease-out;
}

/* Asegurar que el modal esté por encima de todo */
.fixed.inset-0 {
  position: fixed !important;
}

/* Responsivo para móvil */
@media (max-width: 640px) {
  .max-w-7xl {
    max-width: 100%;
    margin: 0;
  }

  .max-h-\[95vh\] {
    max-height: 100vh;
  }

  .rounded-lg {
    border-radius: 0;
  }

  .p-4 {
    padding: 0;
  }
}
</style>